/**
 * DataGridKeyboardHelp
 *
 * Popover showing all keyboard shortcuts for the data grid.
 * Triggered by the "?" key or a toolbar button.
 */

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Keyboard } from 'lucide-react';
import type { DataGridLabels } from './dataGridLabels';
import { DEFAULT_DATA_GRID_LABELS } from './dataGridLabels';

interface DataGridKeyboardHelpProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  labels?: Partial<DataGridLabels>;
}

interface ShortcutRow {
  keys: string;
  description: string;
}

export function DataGridKeyboardHelp({ open, onOpenChange, labels }: DataGridKeyboardHelpProps) {
  const l = { ...DEFAULT_DATA_GRID_LABELS, ...labels };
  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.userAgent);
  const mod = isMac ? '⌘' : 'Ctrl';

  const sections: { title: string; shortcuts: ShortcutRow[] }[] = [
    {
      title: l.dgNavigation,
      shortcuts: [
        { keys: 'Tab / Shift+Tab', description: l.dgShortcutTab },
        { keys: 'Enter', description: l.dgShortcutEnter },
        { keys: 'Escape', description: l.dgShortcutEscape },
        { keys: '↑ ↓ ← →', description: l.dgShortcutArrows },
        { keys: 'Home', description: l.dgShortcutHome },
        { keys: 'End', description: l.dgShortcutEnd },
        { keys: `${mod}+Home`, description: l.dgShortcutCtrlHome },
        { keys: `${mod}+End`, description: l.dgShortcutCtrlEnd },
        { keys: 'Page Up', description: l.dgShortcutPageUp },
        { keys: 'Page Down', description: l.dgShortcutPageDown },
      ],
    },
    {
      title: l.dgSelection,
      shortcuts: [
        { keys: 'Shift+↑↓←→', description: l.dgShortcutShiftArrows },
        { keys: `Shift+${mod}+↑↓←→`, description: l.dgShortcutShiftCtrlArrows },
        { keys: 'Shift+Home', description: l.dgShortcutShiftHome },
        { keys: 'Shift+End', description: l.dgShortcutShiftEnd },
      ],
    },
    {
      title: l.dgClipboard,
      shortcuts: [
        { keys: `${mod}+C`, description: l.dgShortcutCtrlC },
        { keys: `${mod}+V`, description: l.dgShortcutCtrlV },
        { keys: `${mod}+X`, description: l.dgShortcutCtrlX },
        { keys: 'Delete', description: l.dgShortcutDelete },
      ],
    },
    {
      title: l.dgUndoRedo,
      shortcuts: [
        { keys: `${mod}+Z`, description: l.dgShortcutCtrlZ },
        { keys: `${mod}+Shift+Z`, description: l.dgShortcutCtrlShiftZ },
      ],
    },
  ];

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0"
          aria-label={l.dgKeyboardHelp}
        >
          <Keyboard className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-4" align="end">
        <h3 className="text-sm font-semibold mb-3">{l.dgKeyboardHelp}</h3>
        <div className="space-y-3">
          {sections.map((section) => (
            <div key={section.title}>
              <h4 className="text-xs font-medium text-muted-foreground mb-1.5">
                {section.title}
              </h4>
              <div className="space-y-1">
                {section.shortcuts.map((s) => (
                  <div key={s.keys} className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">{s.description}</span>
                    <kbd className="ml-2 shrink-0 rounded-md border border-border bg-muted px-1.5 py-0.5 font-mono text-[10px]">
                      {s.keys}
                    </kbd>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
